import React, { useContext } from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { HBSegmentedControlOptionProps } from './HBSegmentedControl.types';
import { SegmentedControlContext } from './HBSegmentedControl';
import {
    OptionWrapper,
    OptionContent,
    IconWrapper,
    SelectedIndicator,
    HoverBackground,
    ItemContainer
} from './HBSegmentedControl.styles';
import HBText from '../HBText/HBText';
import { TTextType } from '../HBText/HBText';
import { TControlSize } from '../../tokens/types';
import HBTooltip from '../HBTooltip/HBTooltip';

const HBSegmentedControlOption: React.FC<HBSegmentedControlOptionProps> = ({
    value,
    children,
    icon,
    disabled: optionDisabled,
    ariaLabel,
    tooltipText
}) => {
    const context = useContext(SegmentedControlContext);

    if (!context) {
        throw new Error(
            'HBSegmentedControl.Option must be used within HBSegmentedControl'
        );
    }

    const {
        selectedValue,
        size = 'md',
        disabled: controlDisabled,
        stretchToFill = false,
        layoutId,
        tooltipConfig,
        hoveredValue,
        setHoveredValue
    } = context;

    const isDisabled = controlDisabled || optionDisabled;
    const isSelected = selectedValue === value;

    const textTypeMap: Record<TControlSize, TTextType> = {
        sm: 'body14',
        md: 'body16',
        lg: 'body18'
    };

    const textType = textTypeMap[size];
    const textColor = isDisabled
        ? 'text.disabled'
        : isSelected
        ? 'text.primary'
        : 'text.secondary';

    const itemContent = (
        <ItemContainer $size={size} $disabled={isDisabled}>
            {isSelected && (
                <SelectedIndicator
                    layoutId={layoutId}
                    transition={{
                        type: 'spring',
                        stiffness: 629,
                        damping: 44.9,
                        duration: 267,
                        mass: 1
                    }}
                />
            )}
            {!isSelected && <HoverBackground className="hover-background" />}
            <OptionContent $size={size}>
                {icon && (
                    <IconWrapper
                        $isSelected={isSelected}
                        $isDisabled={isDisabled}
                    >
                        {icon}
                    </IconWrapper>
                )}
                {children && (
                    <HBText type={textType} color={textColor}>
                        {children}
                    </HBText>
                )}
            </OptionContent>
        </ItemContainer>
    );

    const toggleItemTarget = (
        <ToggleGroup.Item
            value={value}
            disabled={isDisabled}
            aria-label={ariaLabel}
            asChild
        >
            {itemContent}
        </ToggleGroup.Item>
    );

    // Disable tooltip on selected item if another item is being hovered
    const shouldDisableTooltip =
        isSelected && hoveredValue !== undefined && hoveredValue !== value;

    // Show tooltip for non-disabled items with tooltipText
    const shouldShowTooltip =
        tooltipText && !isDisabled && !shouldDisableTooltip;

    const wrappedContent = shouldShowTooltip ? (
        <HBTooltip
            target={toggleItemTarget}
            openTrigger="hover"
            onOpenChange={({ isOpen }) => {
                setHoveredValue(isOpen ? value : undefined);
            }}
            targetContainerStyle={{
                borderRadius: 'inherit',
                display: 'flex',
                width: '100%',
                height: '100%'
            }}
            {...tooltipConfig}
        >
            {tooltipText}
        </HBTooltip>
    ) : (
        toggleItemTarget
    );

    return (
        <OptionWrapper $size={size} $stretchToFill={stretchToFill}>
            {wrappedContent}
        </OptionWrapper>
    );
};

export default HBSegmentedControlOption;
